import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AdminPage from "./pages/AdminPage";
import PrivateRoute from "./components/PrivateRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import ContentPage from "./pages/ContentPage";
import LeaguePage from "./pages/LeagueLocker";
import ExplorePage from "./pages/Explore";
import Hotjar from "@hotjar/browser";
import { YahooAuthProvider } from "./contexts/YahooAuthContext";
import YahooCallback from "./components/YahooCallback";
import TeamSelection from "./components/leaguePages/TeamSelection";
import ChallengeDetailPage from "./pages/ChallengeDetailPage";
import { ModalProvider } from "./contexts/ModalContext";
import ScrollToTop from "./components/ui/ScrollToTop";
import { Helmet } from "react-helmet";
import { FirebaseAnalyticsProvider } from "./services/AnalyticsProvider";
import MarkdownProvider from "./contexts/MarkdownContext";
import WheelOfShamePage from "./pages/WheelOfShamePage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AboutUs from "./pages/AboutUs";
import HomePage from "./pages/NewHome";
import { ParallaxProvider } from "react-scroll-parallax";
import BlogDetailPage from "./pages/blogPage/BlogDetailPage";
import DraftIdeas from "./pages/DraftIdeas";
import DraftIdeasContentPage from "./pages/DraftIdeasContentPage";
import Join from "./pages/Join";
import SignupLoginPage from "./pages/SignupLoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import CreateLeaguePage from "./pages/CreateLeaguePage";
import VerifyUserPage from "./pages/VerifyUserPage";
import DashboardPage from "./pages/DashboardPage";
import LeagueLockerPage from "./pages/LeagueLockerPage";
import SeasonChallengePage from "./pages/SeasonChallengePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LeagueLockerGuard from "./components/guards/LeagueLockerGuard";
import LoserOfTheWeekDetailPage from "./pages/LoserOfTheWeekDetailPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LockerSignupPage from "./pages/LockerSignupPage";
import EditLeaguePage from "./pages/EditLeaguePage";
import EditMerchPage from "./pages/EditMerchPage";
import EditLeagueRulesPage from "./pages/EditLeagueRulesPage";
import ManageLeaguePage from "./pages/ManageLeaguePage";
import ManageTeamPage from "./pages/ManageTeamPage";
import EditMyTeamPage from "./pages/EditMyTeamPage";
import ManageChallengePage from "./pages/ManageChallengePage";

const siteId = 5151409;
const hotjarVersion = 6;

Hotjar.init(siteId, hotjarVersion);

const AppContent = () => {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/join" element={<Join />} />
      <Route path="/home" element={<SignupLoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/leaguelocker" element={<LockerSignupPage />} />
      <Route
        path="/admin"
        element={
          <PrivateRoute component={AdminPage} allowedRoles={["admin"]} />
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute component={DashboardPage} allowedRoles={["user"]} />
        }
      />
      <Route path="/punishments/:slug" element={<ContentPage />} />
      <Route path="/punishments" element={<ExplorePage />} />
      <Route path="/draft-ideas" element={<DraftIdeas />} />
      <Route path="/draft-ideas/:slug" element={<DraftIdeasContentPage />} />
      <Route path="/challenges/:id" element={<ChallengeDetailPage />} />
      <Route path="/articles" element={<BlogDetailPage />} />
      <Route path="/articles/:categoryId/:id" element={<BlogDetailPage />} />
      <Route path="/articles/:id" element={<BlogDetailPage />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/league/:id/*" element={<LeaguePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route path="/yahoo-callback" element={<YahooCallback />} />
      <Route path="/league/:id/join" element={<TeamSelection />} />
      <Route path="/wheelofshame" element={<WheelOfShamePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/locker-signup" element={<LockerSignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/*" element={<ResetPasswordPage />} />
      <Route path="/verify/*" element={<VerifyUserPage />} />
      <Route
        path="/season-challenge/:leagueUrl"
        element={
          <PrivateRoute
            component={SeasonChallengePage}
            allowedRoles={["user"]}
          />
        }
      />
      <Route
        path="/manage-challenge/:leagueUrl/:challengeId?"
        element={
          <PrivateRoute
            component={ManageChallengePage}
            allowedRoles={["user"]}
          />
        }
      />
      <Route
        path="/create-league"
        element={
          <PrivateRoute component={CreateLeaguePage} allowedRoles={["user"]} />
        }
      />
      <Route
        path="/manage-league/:leagueSlug"
        element={
          <PrivateRoute component={ManageLeaguePage} allowedRoles={["user"]} />
        }
      />
      <Route
        path="/manage-league/:leagueSlug/edit-league"
        element={
          <PrivateRoute component={EditLeaguePage} allowedRoles={["user"]} />
        }
      />
      <Route
        path="/manage-league/:leagueSlug/edit-teams"
        element={
          <PrivateRoute component={ManageTeamPage} allowedRoles={["user"]} />
        }
      />
      <Route
        path="/manage-league/:leagueSlug/edit-rules"
        element={
          <PrivateRoute
            component={EditLeagueRulesPage}
            allowedRoles={["user"]}
          />
        }
      />
      <Route
        path="/manage-league/:leagueSlug/edit-my-team"
        element={
          <PrivateRoute component={EditMyTeamPage} allowedRoles={["user"]} />
        }
      />
      <Route
        path="/manage-league/:leagueSlug/manage-merch"
        element={
          <PrivateRoute component={EditMerchPage} allowedRoles={["user"]} />
        }
      />

      <Route element={<LeagueLockerGuard />}>
        <Route
          path="/:leagueUrl/:tab?/:feedId?"
          element={<LeagueLockerPage />}
        />
      </Route>

      <Route path="/lotw/:id" element={<LoserOfTheWeekDetailPage />} />
    </Routes>
  );
};

const App: React.FC = () => {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <ParallaxProvider>
        <Router>
          <Helmet>
            <title>Fantasy Loser</title>
            <meta
              name="description"
              content="The premier site for fantasy sports and fantasy punishments"
            />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0"
            />
          </Helmet>
          <YahooAuthProvider>
            <AuthProvider>
              <ThemeProvider>
                <MarkdownProvider>
                  <ScrollToTop />
                  <ModalProvider>
                    <FirebaseAnalyticsProvider>
                      <AppContent />
                    </FirebaseAnalyticsProvider>
                  </ModalProvider>
                </MarkdownProvider>
              </ThemeProvider>
            </AuthProvider>
          </YahooAuthProvider>
        </Router>
      </ParallaxProvider>
    </QueryClientProvider>
  );
};

export default App;
